"use client";

import Landing from "@/containers/framerlanding/Landing";
import NativeLanding from "@/containers/framerlanding/NativeLanding";
import PremiumLanding from "@/containers/framerlanding/PremiumLanding";
import PremiumParentHub from "@/containers/ouders/PremiumParentHub";
import { CheckoutContextProvider } from "@/context/CheckoutContext";
import { useAppContext } from "@/context/AppContext";
import { useAuthContext } from "@/context/AuthContext";
import { useParentStudentContext } from "@/context/ParentStudentContext";
import { useLayoutMode } from "@/hooks/useLayoutMode";
import { SLUG_PATTERN, MAX_SLUG_LENGTH, flushInfluencerAttribution } from "@/lib/influencerAttribution";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";

export default function HomePage() {
  const { layoutMode } = useLayoutMode();

  if (!layoutMode) return;

  if (layoutMode === "native") {
    return <NativeLanding />;
  } else {
    return <WebLanding />;
  }
}

function WebLanding() {
  const { userData, isPremium, isParent, isLoggedIn } = useAuthContext();
  const { api } = useAppContext();
  const { links, fetchInvites, fetchLinks } = useParentStudentContext();

  const router = useRouter();
  const searchParams = useSearchParams();
  const attributionHandled = useRef(false);

  useEffect(() => {
    // Store influencer ref in sessionStorage for attribution after login
    const ref = searchParams.get("ref");
    if (ref && ref.length <= MAX_SLUG_LENGTH && SLUG_PATTERN.test(ref) && !attributionHandled.current) {
      attributionHandled.current = true;
      try { sessionStorage.setItem("influencer_ref", ref); } catch {}

      // If user is already logged in, flush attribution now (AuthContext only handles login transitions)
      if (userData && !isPremium) {
        flushInfluencerAttribution(api);
      }

      // Clean ref from URL to prevent re-attribution on re-renders
      router.replace("/");
      return;
    }
  }, [isParent, userData, router, searchParams, api]);

  // Fetch parent-student data when logged-in parent
  const [parentDataLoaded, setParentDataLoaded] = useState(false);
  useEffect(() => {
    if (isLoggedIn && isParent) {
      Promise.all([fetchInvites(), fetchLinks()])
        .then(() => setParentDataLoaded(true))
        .catch(() => setParentDataLoaded(true));
    }
  }, [fetchInvites, fetchLinks, isLoggedIn, isParent]);

  // Premium parent: show parent hub with subscriptions
  if (isParent && userData) {
    // Subscriptions on userData are available immediately
    const hasDirectSubscription =
      (userData.subscriptions && userData.subscriptions.length > 0) ||
      userData?.pending_subscription;

    if (hasDirectSubscription) {
      return (
        <CheckoutContextProvider>
          <PremiumParentHub />
        </CheckoutContextProvider>
      );
    }

    // Links require a fetch — wait for it before deciding
    if (!parentDataLoaded) return null;

    if (links.length > 0) {
      return (
        <CheckoutContextProvider>
          <PremiumParentHub />
        </CheckoutContextProvider>
      );
    }
  }

  // Premium student
  if (isPremium) {
    return <PremiumLanding />;
  }

  // Non-premium (students and non-premium parents) — Landing is parent-aware
  return <Landing />;
}
